import { mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import { CharacterCount, Placeholder } from "@tiptap/extensions";
import { GenericBlock, GenericTextBlock } from "./extensions/GenericBlock";
import { ImageNode } from "./extensions/ImageNode";
import { PreservedAttributes } from "./extensions/PreservedAttributes";
import { Indent } from "./extensions/Indent";
import { PreservedSpan } from "./extensions/PreservedSpan";
import { PreservedStyles } from "./extensions/PreservedStyles";
import { PageBreak } from "./extensions/PageBreak";
import { TableAttributes } from "./extensions/TableAttributes";
import { WordShortcuts } from "./extensions/WordShortcuts";

// StarterKit v3 already carries bold, italic, underline, strike, code,
// headings, blockquote, code block, horizontal rule, hard break, link,
// bullet/ordered lists, paragraph, text, document and undo/redo. Do not
// install those as separate packages - two copies of an extension registers
// the same node name twice and Tiptap throws at construction.

const ALIGN_TYPES = ["heading", "paragraph"];

/**
 * Everything the current TinyMCE toolbars can do apart from tables.
 * Use for plain prose fields.
 */
export function basicPreset(options = {}) {
  return [
    StarterKit.configure({
      // All six. The toolbar offers no heading buttons, so levels exist only
      // to decide what survives a round trip - and the notices use <h3> and
      // <h4>, which a shorter list would silently flatten to paragraphs.
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      // Clicks are handled on the wrapper instead - see RichTextEditor.js.
      // Tiptap renders inline (no iframe), so a real navigation inside the
      // editor would take the whole SPA with it.
      link: { openOnClick: false },
      ...(options.starterKit || {}),
    }),
    TextAlign.configure({ types: ALIGN_TYPES }),
    // PreservedStyles must come before Indent: Indent reads and writes the
    // `style` attribute that PreservedStyles declares.
    PreservedStyles.configure({
      types: [
        "paragraph",
        "heading",
        "listItem",
        "genericBlock",
        "genericTextBlock",
      ],
    }),
    Indent,
    // Writes `page-break-before` into the same `style` attribute
    // PreservedStyles declares, so it has to sit after it too.
    PageBreak,
    PreservedSpan,
    ImageNode,
    CharacterCount,
    // Callbacks come from RichTextEditor's onSave/onPrint props.
    WordShortcuts.configure({
      onSave: options.onSave || null,
      onPrint: options.onPrint || null,
    }),
    ...(options.placeholder
      ? [Placeholder.configure({ placeholder: options.placeholder })]
      : []),
  ];
}

/**
 * Tiptap's Table serialises a <colgroup> of pixel min-widths ahead of the
 * body. Ours does not: every stored table in this app carries its column split
 * as `width: %` on the cells, which is what bac/html_docx.py reads, and an
 * extra colgroup of px widths would both fight that and add markup no existing
 * document has. Column widths therefore live on the cells, exactly as TinyMCE
 * wrote them.
 */
const PlainTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      "table",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      ["tbody", 0],
    ];
  },
});

/**
 * Basic plus tables. Use for notices, factsheets and orders - anything whose
 * stored HTML contains a <table>.
 */
export function documentPreset(options = {}) {
  return [
    ...basicPreset(options),
    PlainTable.configure({
      // Column resizing OFF. The order sheet's 30/70 split is load-bearing
      // (the docx renderer reads the column widths), and an arbitrator
      // dragging a border would silently rewrite it. This is the equivalent
      // of TinyMCE's table_column_resizing: "preservetable".
      resizable: false,
      ...(options.table || {}),
    }),
    TableRow,
    TableHeader,
    TableCell,
    TableAttributes,
  ];
}

/**
 * Everything documentPreset has, plus whatever it takes to round-trip markup
 * the schema has no node for.
 *
 * For the factsheet, which is PDF-layout HTML: `factsheetHtml.js` keeps the
 * document's own <style> block verbatim and never filters the body
 * (`valid_elements: "*[*]"`), because the classes, inline styles and container
 * structure ARE the layout. A plain schema drops all of that silently on save.
 *
 * GenericBlock/GenericTextBlock carry the containers; PreservedAttributes
 * carries class, id and data-*; PreservedStyles and PreservedSpan (already in
 * basicPreset) carry the inline CSS.
 */
export function factsheetPreset(options = {}) {
  return [
    ...documentPreset(options),
    GenericBlock,
    GenericTextBlock,
    PreservedAttributes,
  ];
}
